/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ChangeStorageTierParams } from '../models/ChangeStorageTierParams';
import type { DeadLetterGroupResponse } from '../models/DeadLetterGroupResponse';
import type { JobResponse } from '../models/JobResponse';
import type { StorageTierPreviewResponse } from '../models/StorageTierPreviewResponse';
import type { TierDistributionRowResponse } from '../models/TierDistributionRowResponse';
import type { TieringJobResponse } from '../models/TieringJobResponse';
import type { TieringPauseStateResponse } from '../models/TieringPauseStateResponse';
import type { TieringRowActionResponse } from '../models/TieringRowActionResponse';
import type { TierStatusSummaryRowResponse } from '../models/TierStatusSummaryRowResponse';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class StorageOpsService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Request a storage tier change for every asset a search matches
     * @returns JobResponse Successful Response
     * @throws ApiError
     */
    public requestStorageTierChange({
        requestBody,
    }: {
        requestBody: ChangeStorageTierParams,
    }): CancelablePromise<JobResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/assets/storage-tier',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Dry-run a storage tier change: counts, bytes, and fee exposure
     * @returns StorageTierPreviewResponse Successful Response
     * @throws ApiError
     */
    public previewStorageTierChangeRequest({
        requestBody,
    }: {
        requestBody: ChangeStorageTierParams,
    }): CancelablePromise<StorageTierPreviewResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/assets/storage-tier/preview',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Tier distribution: active link counts and bytes by tier
     * @returns TierDistributionRowResponse Successful Response
     * @throws ApiError
     */
    public getTierDistribution(): CancelablePromise<Array<TierDistributionRowResponse>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/assets/storage-tier/distribution',
        });
    }

    /**
     * In-flight tiering rows by status, including rehydrations
     * @returns TierStatusSummaryRowResponse Successful Response
     * @throws ApiError
     */
    public getTierStatusSummary(): CancelablePromise<Array<TierStatusSummaryRowResponse>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/assets/storage-tier/status-summary',
        });
    }

    /**
     * Recent tiering jobs with per-row status counts
     * @returns TieringJobResponse Successful Response
     * @throws ApiError
     */
    public listTieringJobs({
        limit = 50,
    }: {
        limit?: number,
    }): CancelablePromise<Array<TieringJobResponse>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/assets/storage-tier/jobs',
            query: {
                'limit': limit,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Failed tiering rows grouped by originating job
     * @returns DeadLetterGroupResponse Successful Response
     * @throws ApiError
     */
    public listDeadLetters(): CancelablePromise<Array<DeadLetterGroupResponse>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/assets/storage-tier/dead-letters',
        });
    }

    /**
     * Flip failed automatic-tiering (jobless) rows back to requested
     * @returns TieringRowActionResponse Successful Response
     * @throws ApiError
     */
    public retryAutomaticFailedRows(): CancelablePromise<TieringRowActionResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/assets/storage-tier/dead-letters/retry-automatic',
        });
    }

    /**
     * Flip a job's failed rows back to requested
     * @returns TieringRowActionResponse Successful Response
     * @throws ApiError
     */
    public retryFailedRows({
        jobPublicId,
    }: {
        jobPublicId: string,
    }): CancelablePromise<TieringRowActionResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/assets/storage-tier/jobs/{job_public_id}/retry-failed',
            path: {
                'job_public_id': jobPublicId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Re-request a cancelled job's cancelled rows at their marked tier
     * @returns TieringRowActionResponse Successful Response
     * @throws ApiError
     */
    public retierCancelledRows({
        jobPublicId,
    }: {
        jobPublicId: string,
    }): CancelablePromise<TieringRowActionResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/assets/storage-tier/jobs/{job_public_id}/retier-cancelled',
            path: {
                'job_public_id': jobPublicId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Cancel outstanding tiering rows created by a marker job
     * @returns TieringRowActionResponse Successful Response
     * @throws ApiError
     */
    public cancelPendingTieringRows({
        jobPublicId,
    }: {
        jobPublicId: string,
    }): CancelablePromise<TieringRowActionResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/assets/storage-tier/jobs/{job_public_id}/cancel-pending',
            path: {
                'job_public_id': jobPublicId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Whether tiering workers are globally paused
     * @returns TieringPauseStateResponse Successful Response
     * @throws ApiError
     */
    public getPauseState(): CancelablePromise<TieringPauseStateResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/assets/storage-tier/pause',
        });
    }

    /**
     * Set the global tiering pause flag (the kill switch)
     * @returns TieringPauseStateResponse Successful Response
     * @throws ApiError
     */
    public setPauseState({
        requestBody,
    }: {
        requestBody: TieringPauseStateResponse,
    }): CancelablePromise<TieringPauseStateResponse> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/assets/storage-tier/pause',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
